import { useState, useCallback, useEffect } from "react";
import {
  DateInput,
  Button,
  InputField,
  DropDownInput,
  OptionGroup,
  DataTable,
} from "@/components";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import WeightDisputeForm from "./WeightDisputeForm";
import { RightWrapper } from "../../../components";
import { IoIosChatboxes } from "react-icons/io";
import { AiOutlineCheck } from "react-icons/ai";
import ColumnHelper from "@/utils/ColumnHelper";
import { useMemo } from "react";
import { useFilteredData, useAlert, useConfirmationDialog } from "@/";
import { RiFileExcel2Line } from "react-icons/ri";
import {
  fetchWeightDisputes,
  selectAllWeightDisputes,
  takeWeightDisputeAction,
} from "../../../app/slices";
import { useSelector, useDispatch } from "react-redux";
import { subDays } from "date-fns";

const WeightDispute = () => {
  const { alert, AlertDialog: Dialog } = useAlert();
  const { confirm, ConfirmationModal } = useConfirmationDialog();

  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;

  const [filters, setFilters] = useState({
    productName: "",
    courierName: "",
    action: "",
    awbs: "",
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);
  const dispatch = useDispatch();

  const options = [
    {
      label: "Accept Weight",
      value: "accept_weight",
      icon: <AiOutlineCheck />,
    },
    {
      label: "Raise Dispute",
      value: "raise_dispute",
      icon: <IoIosChatboxes />,
    },
  ];
  const handleOptionClick = (value) => {
    if (value === "raise_dispute") {
      setIsSheetOpen(true);
    } else if (value === "accept_weight") {
      // Handle accept weight logic here
      console.log("Accepting weight for selected rows:", selectedRows);
      handleDisputeAccept();
    }
  };

  const handleDisputeAccept = async () => {
    const shipmentIds = selectedRows.map((row) => row._id);
    await confirm("Are you sure you want continue?");
    try {
      const action = await dispatch(takeWeightDisputeAction(shipmentIds));
      if (takeWeightDisputeAction.fulfilled.match(action)) {
        await alert("Disputed Accepted...");
        console.log("Dispute Action Response:", action.payload);
      } else if (takeWeightDisputeAction.rejected.match(action)) {
        console.error("Error taking dispute action:", action.payload);
        alert(
          `Failed to take dispute action : ${action.payload || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("Error take dispute action:", error);
      alert("An unexpected error occurred while taking dispute action");
    }
    console.log(shipmentIds);
  };

  const fetchData = async () => {
    const response = await dispatch(
      fetchWeightDisputes({
        startDate: filters.date.from,
        endDate: filters.date.to,
      })
    );
    console.log("response::::::", response);
  };
  useEffect(() => {
    fetchData();
  }, [appliedFilters.date, dispatch]);

  const weightDisputes = useSelector(selectAllWeightDisputes) || [];

  const courier = useSelector((state) => state.app?.appData?.courier) || [];

  // Courier Options for DropDownInput
  const courierOptions = useMemo(
    () => courier.map((c) => ({ value: c._id, label: c.name })),
    []
  );

  // Filter configuration
  const filterConfig = useMemo(
    () => ({
      productName: { type: "partial", itemKey: "order.products.productName" },
      courierName: { type: "exact", itemKey: "courierId" },
      action: { type: "exact", itemKey: "disputeStatus" },
      awbs: {
        type: "comma-api",
        itemKey: "awbNumber",
        model: "Shipment",
        endpoint: "/common/search",
      },
      date: { type: "date", itemKey: "disputeDate" },
    }),
    []
  );

  // Memoize filtered data to prevent unnecessary re-renders
  const { data: finalFilteredData, isLoading } = useFilteredData(
    weightDisputes,
    appliedFilters,
    filterConfig
  );

  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Applied  Date",
        accessorKey: "",
        cell: (info) => formatDate(info.row.original.disputeDate),
        meta: {
          tooltip: (row) => row.disputeDate || "N/A",
        },
      }),
      ColumnHelper({
        header: "Entered Weight",
        accessorKey: "actualWeight",
        cell: (info) => `${info.row.original.order.physicalWeight} kg`,
        meta: {
          tooltip: (row) => `${row.order.physicalWeight} kg`,
        },
      }),
      ColumnHelper({
        header: "Dimensions (in cm)",
        accessorKey: "dimension",
        cell: (info) => {
          const { length, breadth, height } =
            info.row.original.order.dimensions;
          return `${length} x ${breadth} x ${height}`;
        },
        meta: {
          tooltip: (row) => {
            const { length, breadth, height } = row.order.dimensions;
            return `${length} x ${breadth} x ${height}`;
          },
        },
      }),
      ColumnHelper({
        header: "Applied Weight",
        accessorKey: "appliedWeight",
        cell: (info) => `${info.row.original.appliedWeight} kg`,
        meta: {
          tooltip: (row) => `${row.appliedWeight} kg`,
        },
      }),
      ColumnHelper({
        header: "Shipment ID",
        accessorKey: "shipmentId",
        meta: {
          tooltip: (row) => row.shipmentId,
        },
      }),
      ColumnHelper({
        header: "Products",
        accessorKey: "products",
        cell: (info) => {
          const products = info.row.original.order.products
            .map((product) => product.productName)
            .join(", ");
          return <span>{products}</span>;
        },
        meta: {
          tooltip: (row) =>
            row.order.products.map((p) => p.productName).join(", "),
        },
      }),
      ColumnHelper({
        header: "Shipping Charge",
        accessorKey: "freightCharge",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.freightCharge)}
          </span>
        ),
        meta: {
          tooltip: (row) => (
            <span className="font-nunito">
              {formatCurrency(row.freightCharge)}
            </span>
          ),
        },
      }),
      ColumnHelper({
        header: "Courier",
        accessorKey: "courierId",
        cell: (info) =>
          courier.find((c) => c._id === info.row.original.courierId)?.name,
        meta: {
          tooltip: (row) => courier.find((c) => c._id === row.courierId)?.name,
        },
      }),
      ColumnHelper({
        header: "AWB",
        accessorKey: "awbNumber",
        meta: {
          tooltip: (row) => row.awbNumber,
        },
      }),
      ColumnHelper({
        header: "Action",
        accessorKey: "",
        cell: (info) => (
          <span className="font-bold font-nunito text-secondary/95">
            {info.row.original.disputeStatus}
          </span>
        ),
        meta: {
          tooltip: (row) => row.disputeStatus,
        },
      }),
    ],
    []
  );

  // Handle filter changes
  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);
  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);

  // Apply filters without resetting raw data
  const handleApply = useCallback(() => {
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  // Reset filters properly
  const handleReset = useCallback(() => {
    const initialFilters = {
      productName: "",
      courierName: "",
      action: "",
      awbs: "",
      date: { from: startOfMonthDate, to: endOfMonthDate },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);
  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 mt-2 px-2 relative pb-20 md:pb-4">
        {/* Filters Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <DateInput
            label="Date Filter"
            placeholder="Select From & To date"
            mode="range"
            value={filters.date}
            onChange={(value) => handleChange("date", value)}
          />
          <InputField
            label="Product Name"
            placeholder="Enter Product Name"
            name="productName"
            value={filters.productName}
            onChange={(value) => handleChange("productName", value)}
          />
          <DropDownInput
            label="Courier Name"
            placeholder="Select Courier Name"
            options={courierOptions}
            value={filters.courierName}
            onChange={(value) => handleChange("courierName", value)}
          />
          <DropDownInput
            label="Action"
            placeholder="Select Action"
            options={[
              { label: "Accepted", value: "Accepted" },
              { label: "Rejected", value: "Rejected" },
              { label: "Auto Accepted", value: "Auto Accepted" },
              { label: "Pending", value: "Pending" },
              { label: "Raised Dispute", value: "Dispute Raised" },
              { label: "Resolved", value: "Resolved" },
            ]}
            value={filters.action}
            onChange={(value) => handleChange("action", value)}
          />
        </div>

        {/* AWB & Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <InputField
            label="AWBs"
            placeholder="AWBs separated by comma"
            name="awbs"
            value={filters.awbs}
            onChange={(value) => handleChange("awbs", value)}
          />
          <div className="flex w-full md:w-fit gap-2 justify-end md:justify-start">
            <Button
              variant="default"
              size="md"
              onClick={handleApply}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleReset}
              className="h-12"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Option Group Section */}

        {selectedRows.length > 0 && (
          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <OptionGroup
              options={options}
              onClick={handleOptionClick}
              selectedCount={selectedRows.length}
            />
            <SheetContent aria-describedby="">
              <SheetHeader>
                <SheetTitle>Create Weight Dispute</SheetTitle>
                <WeightDisputeForm
                  setSheetOpen={setIsSheetOpen}
                  ids={selectedRows.map((row) => row._id)}
                />
              </SheetHeader>
            </SheetContent>
          </Sheet>
        )}
        <DataTable
          columns={columns}
          tittle="Weight Dispute"
          data={finalFilteredData}
          getSelectedRows={setSelectedRows}
          isLoading={isLoading}
          resetPage={resetPageCounter}
          isRowSelectable={(rowData) => {
            if (rowData.disputeStatus === "Pending") {
              return true;
            }
          }}
          actions={[
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Weight Dispute",
                filename: `weight_dispute_${new Date().getTime()}`,
              },
            },
          ]}
        />
      </div>
      <Dialog />
      <ConfirmationModal />
    </RightWrapper>
  );
};

export default WeightDispute;
